import React, { useState, useEffect } from 'react';
import { auditsApi } from '../services/api';
import { User } from '../contexts/AuthContext';

interface SupervisorMyAuditsProps {
  user: User;
}

interface Audit {
  id: number;
  titulo: string;
  descripcion: string;
  estado: string;
  prioridad: string;
  clienteNombre: string;
  operadorNombre: string;
  fechaCreacion: string;
  fechaTerminacion: string;
  fechaEntrega: string;
  observacionesOperador: string;
  observacionesSupervisor: string;
  observacionesFinales: string;
  archivoWord: string;
  costoFinal: number;
  tiempoTrabajado: number;
  eventos: any[];
}

const SupervisorMyAudits: React.FC<SupervisorMyAuditsProps> = ({ user }) => {
  const [audits, setAudits] = useState<Audit[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');

  useEffect(() => {
    loadMyAudits();
  }, []);

  const loadMyAudits = async () => {
    try {
      setLoading(true);
      const response = await auditsApi.getSupervisorHistory();
      if (response.data.success) {
        setAudits(response.data.data || response.data.auditorias);
      }
    } catch (error) {
      console.error('Error cargando historial del supervisor:', error);
    } finally {
      setLoading(false);
    }
  };

  const filteredAudits = audits.filter(audit => {
    const matchesSearch = 
      audit.titulo.toLowerCase().includes(searchTerm.toLowerCase()) ||
      audit.clienteNombre.toLowerCase().includes(searchTerm.toLowerCase()) ||
      audit.operadorNombre.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesStatus = statusFilter === 'all' || audit.estado === statusFilter;
    
    return matchesSearch && matchesStatus;
  });

  const formatDate = (dateString: string) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleString();
  };

  const formatTime = (minutes: number) => {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return `${hours}h ${mins}m`;
  };

  const getPriorityColor = (prioridad: string) => {
    switch (prioridad) {
      case 'urgente': return '#ef4444';
      case 'prioritaria': return '#f59e0b';
      case 'normal': return '#10b981';
      default: return '#6b7280';
    }
  };

  const getStateColor = (estado: string) => {
    switch (estado) {
      case 'pendiente': return '#9ca3af';
      case 'asignada': return '#3b82f6';
      case 'en_proceso': return '#f59e0b';
      case 'pausada': return '#ef4444';
      case 'terminada': return '#f59e0b';
      case 'entregada': return '#10b981';
      case 'facturada': return '#059669';
      default: return '#6b7280';
    }
  };

  if (loading) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '200px' 
      }}>
        <div className="spinner" />
        <span style={{ marginLeft: '0.5rem' }}>Cargando mis auditorías...</span>
      </div>
    );
  }

  return (
    <div>
      <div style={{ marginBottom: '2rem' }}>
        <h2 style={{ fontSize: '1.5rem', fontWeight: 'bold', marginBottom: '0.5rem' }}>
          👨‍💼 Mis Auditorías como Supervisor
        </h2>
        <p style={{ color: '#6b7280' }}>
          Historial de auditorías donde he participado como supervisor con mis mensajes y comentarios.
        </p>
      </div>

      {/* Filtros */}
      <div style={{ 
        display: 'flex', 
        gap: '1rem', 
        marginBottom: '1.5rem',
        flexWrap: 'wrap'
      }}>
        <div style={{ flex: 1, minWidth: '250px' }}>
          <input
            type="text"
            placeholder="Buscar por título, cliente o operador..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={{
              width: '100%',
              padding: '0.5rem',
              border: '1px solid #d1d5db',
              borderRadius: '6px',
              fontSize: '0.875rem'
            }}
          />
        </div>
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          style={{
            padding: '0.5rem',
            border: '1px solid #d1d5db',
            borderRadius: '6px',
            fontSize: '0.875rem',
            minWidth: '150px'
          }}
        >
          <option value="all">Todos los estados</option>
          <option value="terminada">Terminada</option>
          <option value="entregada">Entregada</option>
          <option value="facturada">Facturada</option>
        </select>
        <div style={{
          padding: '0.5rem 1rem',
          backgroundColor: '#f3f4f6',
          borderRadius: '6px',
          fontSize: '0.875rem',
          fontWeight: '600'
        }}>
          📊 Total: {filteredAudits.length}
        </div>
      </div>

      {filteredAudits.length === 0 ? (
        <div className="card" style={{ textAlign: 'center', padding: '3rem' }}>
          <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>📋</div>
          <h3 style={{ fontSize: '1.25rem', fontWeight: '600', marginBottom: '0.5rem' }}>
            No hay auditorías para mostrar
          </h3>
          <p style={{ color: '#6b7280' }}>
            {searchTerm ? 'No se encontraron auditorías con los filtros aplicados.' : 'Aún no has participado en ninguna auditoría como supervisor.'}
          </p>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
          {filteredAudits.map((audit) => (
            <div key={audit.id} className="card">
              <div style={{ 
                display: 'flex', 
                justifyContent: 'space-between', 
                alignItems: 'flex-start',
                marginBottom: '1rem'
              }}>
                <div style={{ flex: 1 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '0.5rem' }}>
                    <h3 style={{ fontSize: '1.25rem', fontWeight: '600', margin: 0 }}>
                      #{audit.id} - {audit.titulo}
                    </h3>
                    <span style={{
                      backgroundColor: getPriorityColor(audit.prioridad),
                      color: 'white',
                      padding: '0.25rem 0.75rem',
                      borderRadius: '9999px',
                      fontSize: '0.75rem',
                      fontWeight: '600',
                      textTransform: 'uppercase'
                    }}>
                      {audit.prioridad}
                    </span>
                    <span style={{
                      backgroundColor: getStateColor(audit.estado),
                      color: 'white',
                      padding: '0.25rem 0.75rem',
                      borderRadius: '9999px',
                      fontSize: '0.75rem',
                      fontWeight: '600'
                    }}>
                      {audit.estado}
                    </span>
                  </div>
                  
                  <p style={{ color: '#6b7280', marginBottom: '1rem' }}>
                    {audit.descripcion}
                  </p>

                  <div style={{ 
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                    gap: '1rem',
                    fontSize: '0.875rem',
                    marginBottom: '1rem'
                  }}>
                    <div>
                      <strong>Cliente:</strong> {audit.clienteNombre}
                    </div>
                    <div>
                      <strong>Operador:</strong> {audit.operadorNombre}
                    </div>
                    <div>
                      <strong>Tiempo trabajado:</strong> {formatTime(audit.tiempoTrabajado)}
                    </div>
                    <div>
                      <strong>Creada:</strong> {formatDate(audit.fechaCreacion)}
                    </div>
                    <div>
                      <strong>Terminada:</strong> {formatDate(audit.fechaTerminacion)}
                    </div>
                    <div>
                      <strong>Entregada:</strong> {formatDate(audit.fechaEntrega)}
                    </div>
                  </div>

                  {audit.observacionesOperador && (
                    <div style={{ 
                      marginBottom: '1rem',
                      padding: '1rem',
                      backgroundColor: '#f9fafb',
                      borderRadius: '8px',
                      border: '1px solid #e5e7eb'
                    }}>
                      <strong style={{ display: 'block', marginBottom: '0.5rem' }}>
                        👷 Observaciones del Operador:
                      </strong>
                      <div style={{ fontSize: '0.875rem', whiteSpace: 'pre-line' }}>
                        {audit.observacionesOperador}
                      </div>
                    </div>
                  )}

                  {audit.observacionesSupervisor && (
                    <div style={{ 
                      marginBottom: '1rem',
                      padding: '1rem',
                      backgroundColor: '#fff3cd',
                      borderRadius: '8px',
                      border: '1px solid #ffeeba'
                    }}>
                      <strong style={{ display: 'block', marginBottom: '0.5rem' }}>
                        👨‍💼 Mis Mensajes como Supervisor:
                      </strong>
                      <div style={{ fontSize: '0.875rem', whiteSpace: 'pre-line' }}>
                        {audit.observacionesSupervisor}
                      </div>
                    </div>
                  )}

                  {audit.observacionesFinales && (
                    <div style={{ 
                      padding: '1rem',
                      backgroundColor: '#f0f9ff',
                      borderRadius: '8px',
                      border: '1px solid #0ea5e9'
                    }}>
                      <strong style={{ display: 'block', marginBottom: '0.5rem' }}>
                        📝 Observaciones Finales:
                      </strong>
                      <div style={{ fontSize: '0.875rem', whiteSpace: 'pre-line' }}>
                        {audit.observacionesFinales}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default SupervisorMyAudits;